/**
 * Help Center UI state.
 *
 * Controls the global Help Center panel: whether it's open, which view is
 * showing (browse a section / read an article / send feedback), and the active
 * section/article. Opened from the top-bar "Help Center" menu, from the pop-out
 * "?" helper on each tab, and from "Report a problem" links.
 */

import { create } from 'zustand';

/** Map a main tabId → its Help Center section id. */
export const TAB_TO_HELP_SECTION = {
  dashboard: 'dashboard',
  'user-management-v2': 'user-management',
  'group-management': 'group-management',
  shares: 'server-management',
  admin: 'drive-management',
  'shared-mailboxes': 'shared-mailboxes',
  'printer-management': 'printer-management',
  'policy-finder': 'policy-finder',
  reporting: 'reporting',
  'asset-management': 'asset-management',
  'log-analytics': 'advanced-reporting',
  'security-dashboard': 'security-hub',
  tools: 'host-pools',
  'network-dashboard': 'network',
  'sharepoint-management': 'sharepoint',
  'cost-analysis': 'cost-analysis',
  automations: 'automations',
  'claude-usage': 'claude-usage',
  compliance: 'compliance',
  backups: 'backups',
  apps: 'apps'
};

/** @param {string} tabId */
export function helpSectionForTab(tabId) {
  return TAB_TO_HELP_SECTION[tabId] ?? 'getting-started';
}

/**
 * Reverse of TAB_TO_HELP_SECTION — the tab that owns a help section, so an
 * answer can offer "take me there". Derived from the same map rather than
 * hand-written, so the two can't drift apart. Returns null for sections with no
 * screen of their own (Getting Started, What's New, Report a Problem).
 */
const HELP_SECTION_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_HELP_SECTION).map(([tabId, section]) => [section, tabId])
);

/** @param {string} sectionId @returns {string | null} */
export function tabForHelpSection(sectionId) {
  return HELP_SECTION_TO_TAB[sectionId] ?? null;
}

export const useHelpStore = create((set, get) => ({
  open: false,
  /** 'browse' | 'article' | 'feedback' */
  view: 'browse',
  section: null,
  articleId: null,
  feedbackType: 'bug',

  /** Open the Help Center. Pass a section to land on it, an articleId to open it. */
  openHelp({ section = null, articleId = null } = {}) {
    set({ open: true, view: articleId ? 'article' : 'browse', section, articleId });
  },

  /** Open straight to the feedback form (optionally pre-selecting a section/type). */
  openFeedback({ section = null, type = 'bug' } = {}) {
    set({ open: true, view: 'feedback', section: section ?? get().section, feedbackType: type });
  },

  goToSection(section) {
    set({ view: 'browse', section, articleId: null });
  },
  goToArticle(articleId, section) {
    set({ view: 'article', articleId, section: section ?? get().section });
  },
  goToFeedback() {
    set({ view: 'feedback' });
  },
  setFeedbackType(feedbackType) {
    set({ feedbackType });
  },

  close() {
    set({ open: false });
  }
}));
